import { useAuth } from "@dl/web-core-js/auth";
import { PageHeader } from "@dl/web-core-js/shell";
import { Badge, Button, Dialog, toast } from "@dl/web-core-js/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { ArchiveX, ArrowLeftRight, ClipboardList, RefreshCw, ShieldCheck, SlidersHorizontal } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatNumber, refusalMessage, StockClient } from "../../api/manufacturing";
import { NexpimView } from "../../views";
import { StockMovementsGrid } from "./StockMovementsGrid";

/**
 * The stock ledger, whole.
 *
 * <p>Two checks sit behind one button because they answer the question people
 * actually have — "can I trust these numbers" — in two halves. The drift check
 * is about nexpim: every cached balance against its own movements, and it
 * should always say nothing. The reconciliation is about SIGM: while SIGM is
 * still the system of record, the imported history must reproduce SIGM's own
 * balances, pair for pair.</p>
 */
export function StockPage() {
  const { t } = useTranslation();
  const { http, hasView, canWrite } = useAuth();
  const navigate = useNavigate();
  const allowed = hasView(NexpimView.MANUFACTURING_STOCK);
  const stock = useMemo(() => new StockClient(http), [http]);
  const [checking, setChecking] = useState(false);
  const queryClient = useQueryClient();

  // The shadow sync: SIGM is still the system of record, so its new
  // transactions come across on demand as well as on schedule.
  const sync = useMutation({
    mutationFn: () => stock.syncFromSigm(),
    onSuccess: async (report) => {
      await queryClient.invalidateQueries({ queryKey: ["manufacturing"] });
      toast.success(t("stock.sync.done", "{{n}} new movements from SIGM", { n: report.movements_written }));
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });

  const drift = useQuery({ queryKey: ["manufacturing", "stock", "drift"], queryFn: () => stock.drift(), enabled: checking });
  const reconcile = useQuery({
    queryKey: ["manufacturing", "stock", "reconcile"],
    queryFn: () => stock.reconcile(25),
    enabled: checking,
  });

  if (!allowed) {
    return (
      <>
        <PageHeader title={t("navigation.item.stock", "Stock")} />
        <div className="p-4 lg:p-6">
          <p className="text-[13px] text-ink-muted">
            {t("error.access-denied.detail", "Your account does not have permission to view this page.")}
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={t("navigation.item.stock", "Stock")}
        subtitle={t("stock.subtitle", "Every movement, never edited. Corrections are posted, not typed over.")}
        actions={
          <>
            <Button variant="ghost" onClick={() => setChecking(true)}>
              <ShieldCheck className="size-4" />
              {t("stock.check", "Check the ledger")}
            </Button>
            <Button variant="ghost" onClick={() => void navigate({ to: "/manufacturing/stock/inactive" })}>
              <ArchiveX className="size-4" />
              {t("inactive-stock.open", "Inactive with stock")}
            </Button>
            <Button variant="ghost" onClick={() => void navigate({ to: "/manufacturing/stock/counts" })}>
              <ClipboardList className="size-4" />
              {t("count.open-list", "Counts")}
            </Button>
            {canWrite && (
              <Button variant="ghost" onClick={() => void navigate({ to: "/manufacturing/stock/transfer" })}>
                <ArrowLeftRight className="size-4" />
                {t("stock-transfer.open", "Transfer")}
              </Button>
            )}
            {canWrite && (
              <Button variant="primary" onClick={() => void navigate({ to: "/manufacturing/stock/adjust" })}>
                <SlidersHorizontal className="size-4" />
                {t("stock.adjust.open", "Adjust stock")}
              </Button>
            )}
          </>
        }
      />

      <StockMovementsGrid stateKey="/manufacturing/stock" />

      <Dialog open={checking} onClose={() => setChecking(false)} title={t("stock.check", "Check the ledger")}>
        <div className="flex flex-col gap-4 text-[13px]">
          <section className="flex flex-col gap-1">
            <h3 className="font-medium text-ink">{t("stock.check.drift", "Balances against their own movements")}</h3>
            {drift.isFetching ? (
              <span className="text-ink-muted">{t("common.loading", "Loading…")}</span>
            ) : drift.isError || !drift.data ? (
              // A failed check is not a clean one.
              <span className="text-risk">{refusalMessage(drift.error, t("stock.check.failed", "The check could not be run."))}</span>
            ) : drift.data.length === 0 ? (
              <span className="text-ok">{t("stock.check.drift.ok", "Every balance is exactly the sum of its movements.")}</span>
            ) : (
              <span className="text-risk">
                {t("stock.check.drift.bad", "{{n}} balances disagree with their movements. This is a bug in nexpim, not a stock problem: report it.", {
                  n: (drift.data ?? []).length,
                })}
              </span>
            )}
          </section>

          <section className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <h3 className="font-medium text-ink">{t("stock.check.sigm", "Imported history against SIGM's balances")}</h3>
              {canWrite && (
                <Button variant="ghost" size="sm" className="ml-auto" loading={sync.isPending} onClick={() => sync.mutate()}>
                  <RefreshCw className="size-4" />
                  {t("stock.sync", "Sync from SIGM")}
                </Button>
              )}
            </div>
            {reconcile.isFetching ? (
              <span className="text-ink-muted">{t("common.loading", "Loading…")}</span>
            ) : reconcile.isError || !reconcile.data ? (
              <span className="text-risk">{refusalMessage(reconcile.error, t("stock.check.failed", "The check could not be run."))}</span>
            ) : (
              <>
                <div className="flex flex-wrap gap-2">
                  <Badge size="sm" variant="neutral">
                    {t("stock.check.pairs", "{{n}} part/warehouse pairs", { n: reconcile.data.pairs_compared })}
                  </Badge>
                  <Badge size="sm" variant="success">
                    {t("stock.check.matching", "{{n}} match", { n: reconcile.data.matching })}
                  </Badge>
                  <Badge size="sm" variant={reconcile.data.mismatched === 0 ? "neutral" : "warning"}>
                    {t("stock.check.mismatched", "{{n}} differ", { n: reconcile.data.mismatched })}
                  </Badge>
                </div>
                {reconcile.data.mismatched > 0 && (
                  <>
                    <p className="text-[12px] text-ink-muted">
                      {t("stock.check.sigm.hint", "Usually SIGM has moved since the last sync. Sync from SIGM, then check again.")}
                    </p>
                    <table className="w-full text-[12px] tabular-nums">
                      <thead className="text-left text-[11px] uppercase tracking-wide text-ink-faint">
                        <tr>
                          <th className="py-1">{t("column.part.number", "Number")}</th>
                          <th className="py-1">{t("column.stock.warehouse", "Warehouse")}</th>
                          <th className="py-1 text-right">{t("stock.check.ledger", "Ledger")}</th>
                          <th className="py-1 text-right">SIGM</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reconcile.data.lines.map((line) => (
                          <tr key={line.part_number + line.warehouse_code} className="border-t border-line">
                            <td className="py-1">{line.part_number}</td>
                            <td className="py-1">{line.warehouse_code}</td>
                            <td className="py-1 text-right">{formatNumber(line.ledger_quantity)}</td>
                            <td className="py-1 text-right">{formatNumber(line.sigm_quantity)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}
              </>
            )}
          </section>
        </div>
      </Dialog>
    </>
  );
}
